import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, BehaviorSubject } from 'rxjs';
import { ApplicationTypes } from './util.enum';
import { PwaConnectivityService } from './pwa-connectivity.service';

@Injectable({
  providedIn: 'root'
})
export class AppGlobalService implements OnDestroy {
  private store = inject(Store);
  private pwaConnectivityService = inject(PwaConnectivityService);

  private destroy$ = new Subject<void>();
  public codeIDEInfo$ = new BehaviorSubject<any>(null);
  public sessionId$ = new BehaviorSubject<string>('');
  public readonly chatPending$ = new BehaviorSubject<boolean>(false);

  /** Delegated to PwaConnectivityService — use networkStatusService.isOnline$ directly for new code. */
  public get isOnline$() {
    return this.pwaConnectivityService.isOnline$;
  }

  private data: any = {};
  public readonly closeRepoManagementPopup$ = new Subject<void>();
  public apiStatusJson = { updateFlagContent: '', updateFlag: false, class: 'success' };

  constructor() {
    let recentAppsValue: any = localStorage.getItem('recentApps');

    if (!recentAppsValue) {
      recentAppsValue = '{}';
      localStorage.setItem('recentApps', recentAppsValue);
    }

    let raiModeEnabledForApps: any = JSON.parse(recentAppsValue);
    this.write('raiModeEnabledApps', raiModeEnabledForApps);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getCookie(name: string): string {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || '';
    }
    return '';
  }

  public write(property: string, value: any): void {
    this.data[property] = value;
  }

  public setSessionId(sessionId: string, sessionContext?: any): void {
    this.data['sessionId'] = sessionId;
    if (sessionContext) {
      if (!this.data['sessionData']) {
        this.data['sessionData'] = {};
      }
      this.data['sessionData'][sessionId] = sessionContext;
    }
    this.sessionId$.next(sessionId || '');
  }

  public updateSessionData(sessionId: string, revisedSessionContext: any) {
    if (this.data['sessionId'] && this.data['sessionData'][sessionId]) {
      this.data['sessionData'][sessionId] = { ...this.data['sessionData'][sessionId], ...revisedSessionContext };
    }
  }

  public writeToSessionStorage(property: string, value: any): void {
    sessionStorage.setItem(property, JSON.stringify(value));
  }

  public getFromSessionStorage(property: string): any {
    const value = sessionStorage.getItem(property);

    if (value) {
      return value;
    }

    return '';
  }

  public get(property: string): any {
    return this.data[property] ? this.data[property] : '';
  }

  public requestCloseRepoManagementPopup(): void {
    this.closeRepoManagementPopup$.next();
  }

  public setChatPending(isPending: boolean): void {
    this.chatPending$.next(isPending);
  }

  public getChatPending(): boolean {
    return this.chatPending$.value;
  }

  public getBoolean(property: string, defaultValue = false): boolean {
    return this.data[property] ?? defaultValue;
  }

  public isBetaAccessPrivilegeConsumable(): boolean {
    const isBetaAccessEnabled = this.getBoolean('isBetaAccessEnabled');
    const raiModeEnabledForUser = this.getBoolean('raiModeEnabledForUser');

    return isBetaAccessEnabled && raiModeEnabledForUser;
  }

  public isBetaAccessPrivilegeConsumableForUser(): boolean {
    const isBetaAccessEnabled = this.getBoolean('isBetaAccessEnabled');
    const raiModeEnabledForUser = this.getBoolean('raiModeEnabledForUser');

    return isBetaAccessEnabled && raiModeEnabledForUser;
  }

  public isBetaAccessPrivilegeConsumableForApp(): boolean {
    const isBetaAccessEnabled = this.getBoolean('isBetaAccessEnabled');
    const raiModeEnabledForUser = this.getBoolean('raiModeEnabledForUser');
    const raiModeEnabledForApp = this.isRaiModeEnabledForApp();

    return isBetaAccessEnabled && raiModeEnabledForUser && raiModeEnabledForApp;
  }

  // Shared aliases for component-wide direct access (minimal wiring in consumers)
  public get betaAccessPrivilegeForUser(): boolean {
    return this.isBetaAccessPrivilegeConsumableForUser();
  }

  public get betaAccessPrivilegeForApp(): boolean {
    return this.isBetaAccessPrivilegeConsumableForApp();
  }

  public updateRaiModeEnabledForApp(newValue: boolean) {
    const currentAppId = this.get('appId');
    const raiModeEnabledApps = this.get('raiModeEnabledApps');

    raiModeEnabledApps[currentAppId] = {
      ...(raiModeEnabledApps[currentAppId] || {}),
      ...{
        isRAIModeEnabled: newValue
      }
    };
    this.write('raiModeEnabledApps', raiModeEnabledApps);

    let recentAppsValue: any = localStorage.getItem('recentApps');
    recentAppsValue = JSON.parse(recentAppsValue);
    recentAppsValue[currentAppId] = {
      ...(recentAppsValue[currentAppId] || {}),
      ...{
        isRAIModeEnabled: newValue,
        timeStamp: new Date().getTime()
      }
    };
    localStorage.setItem('recentApps', JSON.stringify(recentAppsValue));
  }

  public isRaiModeEnabledForApp() {
    const raiModeEnabledApps = this.get('raiModeEnabledApps');

    let thisAppInfo = raiModeEnabledApps[this.get('appId')] || {};

    let value = thisAppInfo.isRAIModeEnabled;

    if (typeof value === 'undefined') {
      value = thisAppInfo.isRAIModeEnabled_default;
    }

    return value;
  }

  public updateRaiModeEnabledForAppId(newValue: boolean, appId = '', addtlConfig?: any) {
    const raiModeEnabledApps = this.get('raiModeEnabledApps');

    let paramInfo: any = {
      timeStamp: new Date().getTime()
    };

    paramInfo.isRAIModeEnabled = newValue;

    if (addtlConfig?.updateDefaultValue) {
      paramInfo.isRAIModeEnabled_default = newValue;
    }

    raiModeEnabledApps[appId] = {
      ...(raiModeEnabledApps[appId] || {}),
      ...paramInfo
    };

    this.write('raiModeEnabledApps', raiModeEnabledApps);

    let recentAppsValue: any = localStorage.getItem('recentApps');
    recentAppsValue = JSON.parse(recentAppsValue);
    recentAppsValue[appId] = {
      ...(recentAppsValue[appId] || {}),
      ...paramInfo
    };

    localStorage.setItem('recentApps', JSON.stringify(recentAppsValue));
  }

  public updateRecentApp(config: any) {
    const raiModeEnabledApps = this.get('raiModeEnabledApps');

    let actionInfo: any = {};

    if (config.action === 'publish-submitted') {
      actionInfo.isManuallyPublished = true;
    } else if (config.action === 'publish-completed') {
      actionInfo.isManuallyPublished = false;
    }

    if (config.action === 'updateDefaultValueForRAIMode') {
      actionInfo.isRAIModeEnabled_default = config.defaultValue;
    }

    if (Object.keys(actionInfo).length > 0) {
      raiModeEnabledApps[config.appId] = {
        ...(raiModeEnabledApps[config.appId] || {}),
        ...actionInfo
      };

      this.write('raiModeEnabledApps', raiModeEnabledApps);

      let recentAppsValue: any = localStorage.getItem('recentApps');
      recentAppsValue = JSON.parse(recentAppsValue);
      recentAppsValue[config.appId] = {
        ...(recentAppsValue[config.appId] || {}),
        ...actionInfo
      };

      localStorage.setItem('recentApps', JSON.stringify(recentAppsValue));
    }
  }

  public saveLSAppInfo(module: string, key: string, value: any): void {
    const appId = this.get('appId');
    if (!appId) {
      return;
    }

    let appInfo: any = {};
    try {
      const saved = localStorage.getItem('appInfo');
      if (saved) {
        appInfo = JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse appInfo from localStorage', e);
    }

    if (!appInfo[appId]) {
      appInfo[appId] = {};
    }
    if (!appInfo[appId][module]) {
      appInfo[appId][module] = {};
    }
    appInfo[appId][module][key] = value;

    localStorage.setItem('appInfo', JSON.stringify(appInfo));
  }

  public getLSAppInfo(module: string, key: string): any {
    const appId = this.get('appId');
    if (!appId) {
      return null;
    }

    try {
      const saved = localStorage.getItem('appInfo');
      if (!saved) {
        return null;
      }

      const appInfo = JSON.parse(saved);
      return appInfo?.[appId]?.[module]?.[key] ?? null;
    } catch (e) {
      console.error('Failed to parse appInfo from localStorage', e);
      return null;
    }
  }

  private saveAppInfo(
    module: string,
    key: string | null | undefined,
    value: any,
    options: { overwrite?: boolean } = {}
  ): void {
    const appId = this.get('appId');
    if (!appId) {
      return;
    }

    let appInfo = this.get('appInfo');
    if (!appInfo) {
      appInfo = {};
    }
    if (!appInfo[appId]) {
      appInfo[appId] = {};
    }

    // Clone value to avoid object-by-reference mutations
    const clonedValue = value !== undefined ? JSON.parse(JSON.stringify(value)) : value;

    if (key) {
      if (!appInfo[appId][module]) {
        appInfo[appId][module] = {};
      }
      appInfo[appId][module][key] = clonedValue;
    } else if (options.overwrite) {
      appInfo[appId][module] = clonedValue;
    } else {
      appInfo[appId][module] = { ...(appInfo[appId][module] || {}), ...clonedValue };
    }

    this.write('appInfo', appInfo);
  }

  private getAppInfo(module: string, key?: string | null): any {
    const appId = this.get('appId');
    if (!appId) {
      return null;
    }

    const appInfo = this.get('appInfo');
    const moduleData = appInfo?.[appId]?.[module];

    if (moduleData === undefined || moduleData === null) {
      return null;
    }

    const value = key ? moduleData[key] : moduleData;

    // Return a clone to prevent external modification
    return value !== undefined && value !== null ? JSON.parse(JSON.stringify(value)) : null;
  }

  public updateCodeIDEInfoKey(key: string, value: any): void {
    this.saveAppInfo('codeIDE', key, value);
    this.codeIDEInfo$.next(this.getAppInfo('codeIDE'));
  }

  public updateCodeIDEInfo(value: any): void {
    this.saveAppInfo('codeIDE', null, value, { overwrite: false }); // Merges with existing
    this.codeIDEInfo$.next(this.getAppInfo('codeIDE'));
  }

  public overwriteCodeIDEInfo(value: any): void {
    this.saveAppInfo('codeIDE', null, value, { overwrite: true }); // Overwrites entirely
    this.codeIDEInfo$.next(this.getAppInfo('codeIDE'));
  }

  public getCodeIDEInfo(key?: string | null): any {
    return this.getAppInfo('codeIDE', key);
  }

  public clearAppInfo(): void {
    const appId = this.get('appId');
    if (!appId) {
      return;
    }

    const appInfo = this.get('appInfo');
    if (appInfo && appInfo[appId]) {
      appInfo[appId] = {};
      this.write('appInfo', appInfo);
    }
  }

  public isManuallyPublished(config: any) {
    const raiModeEnabledApps = this.get('raiModeEnabledApps');

    let thisAppInfo = raiModeEnabledApps[config.appId] || {};

    return thisAppInfo.isManuallyPublished;
  }

  public isRaiModeEnabledForAppId(appId = '') {
    const raiModeEnabledApps = this.get('raiModeEnabledApps');

    let thisAppInfo = raiModeEnabledApps[appId] || {};

    return thisAppInfo.isRAIModeEnabled;
  }

  public isIndependentApp(): boolean {
    return this.data['applicationType'] === ApplicationTypes.INDEPENDENT;
  }

  public isMemberApp(): boolean {
    return this.data['applicationType'] === ApplicationTypes.MEMBER;
  }

  public isSuiteApp(): boolean {
    return this.data['applicationType'] === ApplicationTypes.SUITE;
  }
}
